import { useEffect, useState, useMemo } from 'react';
import { Users, Search, RefreshCw, BookOpen, FileWarning, ClipboardList, FilterX } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../ui/Spinner';
import { PageHeader } from '../ui/PageHeader';
import { fmtDateLong } from '../../lib/datetime';

/**
 * Pestaña admin: actividad docente.
 *
 * Para cada docente activo del campus, calculamos:
 *   - Cursos asignados (donde es docente_id o secondary_docente_id)
 *   - Total de alumnos enseñando (suma única en sus cursos)
 *   - Entregas BIM cargadas/revisadas (uploaded_by o author de bim_comentarios)
 *   - Tareas TO-DO creadas
 *   - Comentarios BIM dejados (engagement directo con alumnos)
 *   - Última actividad detectada (max de sus actions)
 *
 * Sirve al admin para ver quién está enseñando activamente y quién no.
 */
interface DocenteRow {
  id:            string;
  fullName:      string;
  email:         string;
  cursosCount:   number;
  alumnosCount:  number;
  bimEntregas:   number;
  bimComentarios:number;
  tareasCreadas: number;
  ultimaActividad: string | null;
}

export function ActividadDocentePage() {
  const [rows, setRows]   = useState<DocenteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  const load = async () => {
    setLoading(true);
    try {
      // 1) Lista de docentes activos
      const { data: docentes } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('role', 'docente')
        .eq('active', true);

      if (!docentes || docentes.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      const docenteIds = docentes.map(d => d.id);

      // 2) Cursos: donde docente o secondary_docente
      const { data: cursos } = await supabase
        .from('courses')
        .select('id, docente_id, secondary_docente_id')
        .or(`docente_id.in.(${docenteIds.join(',')}),secondary_docente_id.in.(${docenteIds.join(',')})`);

      // 3) Enrollments para contar alumnos por docente (vía cursos del docente)
      const allCursoIds = (cursos ?? []).map((c: any) => c.id);
      const { data: enrollments } = allCursoIds.length > 0
        ? await supabase
            .from('course_enrollments')
            .select('course_id, alumno_id')
            .in('course_id', allCursoIds)
            .eq('status', 'active')
        : { data: [] };

      // 4) bim_entregas cargadas por docente
      const { data: bimEntregas } = await supabase
        .from('bim_entregas')
        .select('uploaded_by, updated_at')
        .in('uploaded_by', docenteIds);

      // 5) bim_comentarios escritos por docente
      const { data: bimComentarios } = await supabase
        .from('bim_comentarios')
        .select('author_id, updated_at')
        .in('author_id', docenteIds);

      // 6) Tareas creadas por docente
      const { data: tareas } = await supabase
        .from('todo_tasks')
        .select('created_by, created_at')
        .in('created_by', docenteIds);

      // ─── Agrupación client-side ────────────────────────────────────
      // Mapas docente_id → métricas
      const cursosPorDocente = new Map<string, Set<string>>();
      (cursos ?? []).forEach((c: any) => {
        for (const did of [c.docente_id, c.secondary_docente_id]) {
          if (!did || !docenteIds.includes(did)) continue;
          const set = cursosPorDocente.get(did) ?? new Set<string>();
          set.add(c.id);
          cursosPorDocente.set(did, set);
        }
      });

      // alumnos únicos por docente
      const alumnosPorDocente = new Map<string, Set<string>>();
      (enrollments ?? []).forEach((e: any) => {
        // Para cada enrollment, ver a qué docentes pertenece su curso
        const curso = (cursos ?? []).find((c: any) => c.id === e.course_id);
        if (!curso) return;
        for (const did of [curso.docente_id, curso.secondary_docente_id]) {
          if (!did || !docenteIds.includes(did)) continue;
          const set = alumnosPorDocente.get(did) ?? new Set<string>();
          set.add(e.alumno_id);
          alumnosPorDocente.set(did, set);
        }
      });

      const bimEntregasPorDocente   = new Map<string, number>();
      const ultimoBimEntrega        = new Map<string, string>();
      (bimEntregas ?? []).forEach((b: any) => {
        bimEntregasPorDocente.set(b.uploaded_by, (bimEntregasPorDocente.get(b.uploaded_by) ?? 0) + 1);
        const cur = ultimoBimEntrega.get(b.uploaded_by);
        if (!cur || (b.updated_at && b.updated_at > cur)) ultimoBimEntrega.set(b.uploaded_by, b.updated_at);
      });

      const bimComentariosPorDocente = new Map<string, number>();
      const ultimoBimComentario      = new Map<string, string>();
      (bimComentarios ?? []).forEach((b: any) => {
        if (!b.author_id) return;
        bimComentariosPorDocente.set(b.author_id, (bimComentariosPorDocente.get(b.author_id) ?? 0) + 1);
        const cur = ultimoBimComentario.get(b.author_id);
        if (!cur || (b.updated_at && b.updated_at > cur)) ultimoBimComentario.set(b.author_id, b.updated_at);
      });

      const tareasPorDocente = new Map<string, number>();
      const ultimaTarea      = new Map<string, string>();
      (tareas ?? []).forEach((t: any) => {
        tareasPorDocente.set(t.created_by, (tareasPorDocente.get(t.created_by) ?? 0) + 1);
        const cur = ultimaTarea.get(t.created_by);
        if (!cur || (t.created_at && t.created_at > cur)) ultimaTarea.set(t.created_by, t.created_at);
      });

      const out: DocenteRow[] = docentes.map(d => {
        // Última actividad = max entre las 3 fechas
        const candidates = [
          ultimoBimEntrega.get(d.id),
          ultimoBimComentario.get(d.id),
          ultimaTarea.get(d.id),
        ].filter(Boolean) as string[];
        const ultima = candidates.length > 0 ? candidates.sort().pop()! : null;

        return {
          id:              d.id,
          fullName:        d.full_name || 'Sin nombre',
          email:           d.email,
          cursosCount:     cursosPorDocente.get(d.id)?.size ?? 0,
          alumnosCount:    alumnosPorDocente.get(d.id)?.size ?? 0,
          bimEntregas:     bimEntregasPorDocente.get(d.id) ?? 0,
          bimComentarios:  bimComentariosPorDocente.get(d.id) ?? 0,
          tareasCreadas:   tareasPorDocente.get(d.id) ?? 0,
          ultimaActividad: ultima,
        };
      });

      // Orden: más activos arriba (suma de actions desc)
      out.sort((a, b) =>
        (b.bimEntregas + b.bimComentarios + b.tareasCreadas) -
        (a.bimEntregas + a.bimComentarios + a.tareasCreadas),
      );
      setRows(out);
    } catch (err) {
      console.error('[ActividadDocentePage] error:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return rows;
    return rows.filter(r =>
      r.fullName.toLowerCase().includes(term) ||
      r.email.toLowerCase().includes(term),
    );
  }, [rows, search]);

  // KPIs agregados
  const kpis = useMemo(() => {
    const sinActividad = rows.filter(r =>
      r.bimEntregas === 0 && r.bimComentarios === 0 && r.tareasCreadas === 0,
    ).length;
    const sumCursos    = rows.reduce((a, r) => a + r.cursosCount, 0);
    const sumComentBim = rows.reduce((a, r) => a + r.bimComentarios, 0);
    return {
      total:        rows.length,
      sinActividad,
      sumCursos,
      sumComentBim,
    };
  }, [rows]);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <PageHeader
        eyebrow="Análisis"
        eyebrowIcon={Users}
        title="Actividad docente"
        description="Cuántos cursos enseña cada docente, cuántas entregas BIM cargó, cuántas tareas creó y cuándo fue su última acción."
        actions={
          <button onClick={load} disabled={loading} className="btn-glass" title="Recargar">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Recargar</span>
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="Docentes activos"     value={kpis.total}        accent="text-white" />
        <StatPill label="Sin actividad reciente" value={kpis.sinActividad} accent={kpis.sinActividad > 0 ? 'text-rose-400' : 'text-cubus-lime'} />
        <StatPill label="Cursos cubiertos"      value={kpis.sumCursos}    accent="text-cubus-cyan" />
        <StatPill label="Coment. BIM totales"   value={kpis.sumComentBim} accent="text-cubus-violet" />
      </div>

      <section className="glass-surface rounded-2xl p-4 border border-white/[0.06]">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="search"
            placeholder="Buscar docente…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-cubus-deep border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cubus-lime/40"
          />
        </div>
      </section>

      <section className="glass-surface rounded-2xl border border-white/[0.06] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50">
            <FilterX className="w-10 h-10 mx-auto mb-3 text-white/30" />
            <p className="text-sm">
              {rows.length === 0 ? 'No hay docentes activos en el campus.' : 'Ningún docente coincide con la búsqueda.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-white/40">
                  <th className="text-left py-3 px-4">Docente</th>
                  <th className="text-center py-3 px-4"><BookOpen className="w-3.5 h-3.5 inline" /> Cursos</th>
                  <th className="text-center py-3 px-4">Alumnos</th>
                  <th className="text-center py-3 px-4"><FileWarning className="w-3.5 h-3.5 inline" /> Entregas BIM</th>
                  <th className="text-center py-3 px-4">Coment. BIM</th>
                  <th className="text-center py-3 px-4"><ClipboardList className="w-3.5 h-3.5 inline" /> Tareas creadas</th>
                  <th className="text-left py-3 px-4">Última actividad</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const total = r.bimEntregas + r.bimComentarios + r.tareasCreadas;
                  const inactivo = total === 0;
                  return (
                    <tr key={r.id} className="border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors text-sm">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border ${
                            inactivo
                              ? 'bg-rose-500/10 border-rose-500/25 text-rose-300'
                              : 'bg-cubus-cyan/15 border-cubus-cyan/30 text-cubus-cyan'
                          }`}>
                            {r.fullName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-white font-medium">{r.fullName}</div>
                            <div className="text-[11px] text-white/40">{r.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center text-white/80 font-medium">{r.cursosCount}</td>
                      <td className="py-3 px-4 text-center text-white/80 font-medium">{r.alumnosCount}</td>
                      <td className="py-3 px-4 text-center font-medium text-cubus-lime">{r.bimEntregas}</td>
                      <td className="py-3 px-4 text-center text-white/80">{r.bimComentarios}</td>
                      <td className="py-3 px-4 text-center text-white/80">{r.tareasCreadas}</td>
                      <td className="py-3 px-4 text-xs">
                        {r.ultimaActividad ? (
                          <span className="text-white/70">{fmtDateLong(r.ultimaActividad)}</span>
                        ) : (
                          <span className="text-rose-400 italic">⚠ sin actividad</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatPill({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="glass-surface rounded-xl border border-white/[0.06] px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</p>
      <p className={`text-display text-2xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}
